import { useController, useFormContext } from 'react-hook-form'
import { TextField as MUITextField } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import InputLabel from 'components/InputLabel'
import LetterCounter from 'components/LetterCounter'
import { SizingContainer } from 'components/SizingContainer'
import { useTemplate } from 'utils/hook'
import validationsToRules from 'utils/validationsToRules'

import { TextArea as TextAreaProps } from './interface'

export default function TextArea({
  name,
  size,
  label,
  rows,
  minRows,
  maxRows,
  onFocus,
  onChange,
  placeholder,
  validations,
  defaultValue,
}: TextAreaProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: render(defaultValue ?? ''),
    rules: validationsToRules(validations),
  })

  const maxLengthVal = validations.maxLength?.value
  const minLengthVal = validations.minLength?.value

  const errorMessageId = `${name}-helper-text`

  return (
    <SizingContainer className="text-area-wrap" size={size}>
      <MUITextField
        {...field}
        id={name}
        required={validations.required?.value}
        fullWidth
        multiline
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        inputProps={{
          maxLength: maxLengthVal,
          minLength: minLengthVal,
          'aria-invalid': error !== undefined,
          'aria-describedby': error !== undefined ? errorMessageId : undefined,
        }}
        FormHelperTextProps={{
          id: errorMessageId,
          role: error !== undefined ? 'alert' : undefined,
        }}
        error={error !== undefined}
        helperText={error?.message}
        inputRef={field.ref}
        placeholder={placeholder}
        label={<InputLabel label={label} />}
        onFocus={onFocus}
        onChange={(e) => {
          field.onChange(e)
          onChange?.({ value: e.target.value })
        }}
      />
      {maxLengthVal !== undefined && (
        <LetterCounter value={field.value} maxLengthVal={maxLengthVal} />
      )}
    </SizingContainer>
  )
}
